import React, { useState } from 'react';
import { Calendar, Plus, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import MealCard from './MealCard';
import { useClientIsolation } from '../../hooks/useClientIsolation';

interface MealPlan {
  id: string;
  date: Date;
  meals: {
    breakfast?: string;
    lunch?: string;
    dinner?: string;
    snacks: string[];
  };
  totalNutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    iron: number;
    calcium: number;
  };
  moodEnergy: {
    energyLevel: number;
    moodRating: number;
    notes: string;
  };
}

const MealPlanningPanel: React.FC = () => {
  // CLIENT ISOLATION: Use client isolation hook
  const { clientContext, validateAccess } = useClientIsolation();
  
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterTag, setFilterTag] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const mockMeals = [
    {
      id: '1',
      name: 'Mediterranean Quinoa Bowl',
      description: 'Nutrient-rich quinoa with fresh vegetables, olives, and feta cheese',
      prepTime: 20,
      servings: 2,
      calories: 420,
      protein: 18,
      carbs: 52,
      fat: 14,
      tags: ['High Protein', 'Anti-Inflammatory', 'Heart Healthy'],
      difficulty: 'Easy' as const
    },
    {
      id: '2',
      name: 'Salmon & Sweet Potato',
      description: 'Omega-3 rich salmon with roasted sweet potato and steamed broccoli',
      prepTime: 25,
      servings: 1,
      calories: 380,
      protein: 32,
      carbs: 28,
      fat: 16,
      tags: ['High Protein', 'Bone Health', 'Energy Boost'],
      difficulty: 'Medium' as const
    },
    {
      id: '3',
      name: 'Green Goddess Smoothie',
      description: 'Spinach, avocado, banana, and protein powder for sustained energy',
      prepTime: 5,
      servings: 1,
      calories: 280,
      protein: 25,
      carbs: 32,
      fat: 8,
      tags: ['Energy Boost', 'Mood Support', 'Low Carb'],
      difficulty: 'Easy' as const
    }
  ];

  const nutritionTags = ['All', 'High Protein', 'Low Carb', 'Anti-Inflammatory', 'Heart Healthy', 'Bone Health', 'Energy Boost', 'Mood Support'];

  const getWeekDays = () => {
    const startOfWeek = new Date(currentWeek);
    startOfWeek.setDate(currentWeek.getDate() - currentWeek.getDay());
    
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return date;
    });
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(currentWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newWeek);
  };

  const filteredMeals = mockMeals.filter(meal => {
    const matchesTag = filterTag === 'All' || meal.tags.includes(filterTag);
    const matchesSearch = meal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         meal.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTag && matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>
          Meal Planning
        </h2>
        <button
          className="p-3 rounded-lg transition-all duration-200"
          style={{
            background: 'var(--gradient-primary)',
            color: 'var(--color-white)',
            boxShadow: 'var(--shadow-md)'
          }}
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
            Weekly Meal Plan
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateWeek('prev')}
              className="p-2 rounded-lg transition-all duration-200"
              style={{
                background: 'var(--gradient-card-bg)',
                color: 'var(--color-text-muted)',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigateWeek('next')}
              className="p-2 rounded-lg transition-all duration-200"
              style={{
                background: 'var(--gradient-card-bg)',
                color: 'var(--color-text-muted)',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-6">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <div key={day} className="text-center">
              <div className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-muted)' }}>
                {day}
              </div>
              <button
                onClick={() => setSelectedDate(getWeekDays()[index])}
                className="w-full aspect-square rounded-lg transition-all duration-200 text-sm font-medium"
                style={{
                  background: selectedDate.toDateString() === getWeekDays()[index].toDateString()
                    ? 'var(--gradient-primary)'
                    : 'var(--gradient-card-bg)',
                  color: selectedDate.toDateString() === getWeekDays()[index].toDateString()
                    ? 'var(--color-white)'
                    : 'var(--color-text)',
                  boxShadow: 'var(--shadow-sm)'
                }}
              >
                {getWeekDays()[index].getDate()}
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h4 className="font-semibold" style={{ color: 'var(--color-text)' }}>
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Breakfast', 'Lunch', 'Dinner'].map((mealType) => (
              <div
                key={mealType}
                className="p-4 rounded-lg border-2 border-dashed transition-all duration-200 hover:border-solid cursor-pointer"
                style={{
                  borderColor: 'var(--color-primary)',
                  background: 'rgba(58, 175, 169, 0.05)'
                }}
              >
                <div className="text-center">
                  <h5 className="font-medium mb-2" style={{ color: 'var(--color-text)' }}>
                    {mealType}
                  </h5>
                  <Plus className="h-8 w-8 mx-auto mb-2" style={{ color: 'var(--color-primary)' }} />
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Add meal
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <div className="mb-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
              Meal Library
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
                <input
                  type="text"
                  placeholder="Search meals..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border text-sm"
                  style={{
                    borderColor: 'rgba(0,0,0,0.1)',
                    background: 'var(--color-white)',
                    color: 'var(--color-text)',
                    fontSize: '16px'
                  }}
                />
              </div>
              <select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                className="w-full px-3 py-3 rounded-lg border text-sm"
                style={{
                  borderColor: 'rgba(0,0,0,0.1)',
                  background: 'var(--color-white)',
                  color: 'var(--color-text)',
                  fontSize: '16px'
                }}
              >
                {nutritionTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredMeals.map((meal) => (
            <MealCard
              key={meal.id}
              {...meal}
              onSelect={(mealId) => console.log('Selected meal:', mealId)}
              onToggleFavorite={(mealId) => console.log('Toggled favorite:', mealId)}
            />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6" style={{ boxShadow: 'var(--shadow-lg)' }}>
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
          Daily Nutrition & Wellness Tracking
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>
              Micronutrients
            </h4>
            <div className="space-y-3">
              {[
                { name: 'Fiber', current: 18, target: 25, unit: 'g' },
                { name: 'Iron', current: 12, target: 18, unit: 'mg' },
                { name: 'Calcium', current: 800, target: 1200, unit: 'mg' }
              ].map((nutrient) => (
                <div key={nutrient.name} className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                    {nutrient.name}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 h-2 rounded-full" style={{ background: 'rgba(0,0,0,0.1)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(nutrient.current / nutrient.target) * 100}%`,
                          background: 'var(--color-primary)'
                        }}
                      />
                    </div>
                    <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                      {nutrient.current}/{nutrient.target}{nutrient.unit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium mb-3" style={{ color: 'var(--color-text)' }}>
              Energy & Mood
            </h4>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Energy Level (1-10)
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  defaultValue="7"
                  className="w-full mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Mood Rating (1-10)
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  defaultValue="8"
                  className="w-full mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Notes
                </label>
                <textarea
                  placeholder="How are you feeling today?"
                  className="w-full mt-1 p-2 rounded-lg border text-sm"
                  rows={2}
                  style={{
                    borderColor: 'rgba(0,0,0,0.1)',
                    background: 'var(--color-white)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealPlanningPanel;
